import * as signalR from "@microsoft/signalr";
import { getAccessToken, getCurrentUser } from "./api";
import { devLog } from "./logging";
import { de } from "date-fns/locale";

const rawUrl = process.env.REACT_APP_FUNCTION_APP_URL || "https://localhost:7093/";
const API_BASE_URL = rawUrl.endsWith("/") ? rawUrl : rawUrl + "/";

let workflowConnection = null;

// Workflow SignalR connection for reportBuilder
export async function createWorkflowConnection() {
  if (workflowConnection) {
    return workflowConnection;
  }

  // Get access token and user info for authentication
  let accessToken, currentUser;
  try {
    [accessToken, currentUser] = await Promise.all([getAccessToken(), getCurrentUser()]);
  } catch (error) {
    console.error("❌ Failed to get access token or user info for SignalR:", error);
    throw error;
  }

  workflowConnection = new signalR.HubConnectionBuilder()
    .withUrl(`${API_BASE_URL}hubs/workflowHub`, {
      skipNegotiation: false,
      transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      accessTokenFactory: async () => {
        // Always get a fresh token for SignalR
        try {
          const freshToken = await getAccessToken();
          return freshToken;
        } catch (error) {
          console.error("❌ Failed to get fresh token for SignalR:", error);
          return accessToken; // Fallback to original token
        }
      },
    })
    .withAutomaticReconnect([0, 2000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Information)
    .build();

  // Don't enable event logging here - do it after connection is established

  return workflowConnection;
}

export async function startWorkflowSignalR(eventHandlers = {}, currentWorkflowId = null) {
  // If there's already a connection, stop it first
  if (workflowConnection && workflowConnection.state !== "Disconnected") {
    workflowConnection.stop();
    workflowConnection = null;
  }

  let connection;
  try {
    connection = await createWorkflowConnection();
  } catch (error) {
    console.error("❌ Failed to create SignalR connection:", error);
    if (eventHandlers.onError) {
      eventHandlers.onError(error);
    }
    return null;
  }

  // Set up event handlers based on backend API events
  // WorkflowStarted
  if (eventHandlers.WorkflowStarted) {
    connection.on("WorkflowStarted", (data) => {
      // Filter by workflow ID if provided (check both id and Id for compatibility)
      devLog("WorkflowStarted event received:", data);
      const dataWorkflowId = data.id || data.Id;
      if (currentWorkflowId && dataWorkflowId !== currentWorkflowId) {
        return;
      }
      eventHandlers.WorkflowStarted(data);
    });
  }

  // NodeExecutionStarted
  if (eventHandlers.NodeExecutionStarted) {
    connection.on("NodeExecutionStarted", (data) => {
      devLog("NodeExecutionStarted event received:", data);
      const dataWorkflowId = data.id || data.Id;
      if (currentWorkflowId && dataWorkflowId !== currentWorkflowId) {
        return;
      }
      eventHandlers.NodeExecutionStarted(data);
    });
  }

  // NodeExecutionCompleted
  if (eventHandlers.NodeExecutionCompleted) {
    connection.on("NodeExecutionCompleted", (data) => {
      devLog("NodeExecutionCompleted event received:", data);
      const dataWorkflowId = data.id || data.Id;
      if (currentWorkflowId && dataWorkflowId !== currentWorkflowId) {
        return;
      }
      eventHandlers.NodeExecutionCompleted(data);
    });
  }

  // NodeExecutionFailed
  if (eventHandlers.NodeExecutionFailed) {
    connection.on("NodeExecutionFailed", (data) => {
      devLog("NodeExecutionFailed event received:", data);
      const dataWorkflowId = data.id || data.Id;
      if (currentWorkflowId && dataWorkflowId !== currentWorkflowId) {
        return;
      }
      eventHandlers.NodeExecutionFailed(data);
    });
  }

  // WorkflowCompleted
  if (eventHandlers.WorkflowCompleted) {
    connection.on("WorkflowCompleted", (data) => {
      devLog("WorkflowCompleted event received:", data);
      const dataWorkflowId = data.id || data.Id;
      if (currentWorkflowId && dataWorkflowId !== currentWorkflowId) {
        return;
      }
      eventHandlers.WorkflowCompleted(data);
    });
  }

  // WorkflowFailed
  if (eventHandlers.WorkflowFailed) {
    connection.on("WorkflowFailed", (data) => {
      devLog("WorkflowFailed event received:", data);
      const dataWorkflowId = data.workflowId || data.id || data.Id; // Check workflowId first
      if (currentWorkflowId && dataWorkflowId !== currentWorkflowId) {
        return;
      }
      eventHandlers.WorkflowFailed(data);
    });
  }

  // Set up connection lifecycle handlers
  if (eventHandlers.onDisconnected) {
    connection.onclose((error) => {
      eventHandlers.onDisconnected(error);
    });
  }
  if (eventHandlers.onReconnecting) {
    connection.onreconnecting((error) => {
      eventHandlers.onReconnecting(error);
    });
  }
  if (eventHandlers.onReconnected) {
    connection.onreconnected((connectionId) => {
      eventHandlers.onReconnected(connectionId);
    });
  }
  // Add a comprehensive event logger to capture ALL events
  addSignalREventLogger(connection);

  // Only start connection if it's not already connecting or connected
  if (connection.state === "Disconnected") {
    return connection
      .start()
      .then(() => {
        // Verify event handlers are actually registered
        const handlers = connection._callbacks || {};

        if (eventHandlers.onConnected) {
          eventHandlers.onConnected();
        }
        return connection;
      })
      .catch((err) => {
        console.error("❌ Workflow SignalR Connection Error: ", err);
        console.error("❌ Error details:", {
          name: err.name,
          message: err.message,
          stack: err.stack,
        });
        if (eventHandlers.onError) {
          eventHandlers.onError(err);
        }
        // Don't throw the error to prevent unhandled promise rejection
        return null;
      });
  } else {
    return Promise.resolve(connection);
  }
}

export function addSignalREventLogger(connection) {
  if (!connection) {
    return;
  }

  // Override the SignalR connection's internal message handling to intercept ALL messages
  const originalProcessIncomingData =
    connection.connection?.features?.inherentKeepAlive || connection._processIncomingData;

  // List of all possible backend events to monitor
  const possibleEvents = [
    "WorkflowStarted",
    "WorkflowCompleted",
    "WorkflowFailed",
    "NodeExecutionStarted",
    "NodeExecutionCompleted",
    "NodeExecutionFailed",
    "workflowStarted",
    "workflowCompleted",
    "workflowFailed",
    "nodeExecutionStarted",
    "nodeExecutionCompleted",
    "nodeExecutionFailed",
    "workflow_started",
    "workflow_completed",
    "workflow_failed",
    "node_execution_started",
    "node_execution_completed",
    "node_execution_failed",
    "workflowstarted",
    "workflowcompleted",
    "workflowfailed",
    "nodeexecutionstarted",
    "nodeexecutioncompleted",
    "nodeexecutionfailed",
    "WorkflowUpdate",
    "NodeUpdate",
    "ExecutionUpdate",
    "StatusUpdate",
    "DebugMessage",
    "TestEvent",
    "Connected",
    "Disconnected",
    "Error",
    "Reconnecting",
    "Reconnected",
    "Ping",
    "Pong",
    "Echo",
  ];

  // Register a listener for each possible event

  // Add a periodic check to see if any events have been received
  let eventCount = 0;
  const originalOn = connection.on.bind(connection);
  connection.on = function (eventName, handler) {
    return originalOn(eventName, (...args) => {
      eventCount++;
      return handler(...args);
    });
  };
}

export function stopWorkflowSignalR() {
  if (workflowConnection) {
    // Only try to stop if not already disconnected
    if (workflowConnection.state !== "Disconnected") {
      try {
        workflowConnection.stop();
      } catch (error) {
        console.warn("⚠️ Error stopping SignalR connection:", error);
      }
    }

    workflowConnection = null;
  }
}

export function getWorkflowConnection() {
  return workflowConnection;
}

// Original notify hub connection for backward compatibility
const connection = new signalR.HubConnectionBuilder()
  .withUrl(`${API_BASE_URL}notifyhub`)
  .withAutomaticReconnect()
  .build();

export function startSignalRConnection(onReceiveUpdate) {
  connection
    .start()
    .then(() => console.log("SignalR Connected"))
    .catch((err) => console.error("SignalR Connection Error: ", err));

  connection.on("ReceiveUpdate", (message) => {
    onReceiveUpdate(message);
  });
}
